import React, { useEffect, useState, useContext, useMemo } from 'react'
import styled from 'styled-components'
import TripProgress from '../../TripProgress'
import TripsHeaderInformation from '../../TripsHeaderInformation'
import TripsList from '../../TripsList'
import i18n from 'live/services/i18n'

import { DashboardContext } from 'live/context/dashboard'
import { getLastTrips } from '../../../api/fleet'
import { sort, isObjectEmpty } from '../../../utils'
import TripsContext from '../../../contexts/TripsContext'


type IProps = {
    data: any
    vehicle: any
    show?: boolean
}

const toHeaderData = (data: any = {}, vehicle: any = {}, totalTrips = 0, totalItineraries = 0, totalSchedules = 0) => {
    const { time, car_status, collection, keyMatch } = data
    const { license_plate } = vehicle

    return {
        name: vehicle?.name,
        plate: license_plate,
        totalItineraries,
        totalSchedules,
        totalTrips,
        status: car_status,
        collection,
        keyMatch,
        time
    }
}

const Trips = ({ data = undefined, vehicle = {}, show = false }: IProps): JSX.Element => {
    const context: any = useContext(DashboardContext)
    const { trips, setTrips, appointmentsMap, itinerariesMap, setCenterMap } = useContext(
        TripsContext
    )
    const [tripsDB, setTripsDB] = useState<any[]>()
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [previousMatch, setPreviousKeyMatch] = useState<any>()
    const [previousSpan, setPreviousSpan] = useState<any>()

    const headerData = useMemo(() => {        
        let totalTrips
        let totalItineraries
        let totalSchecules

        if (tripsDB) totalTrips = tripsDB?.length || 0

        if (appointmentsMap) {
            totalSchecules = appointmentsMap && appointmentsMap[vehicle?.id]?.appointments?.length
        }

        if (itinerariesMap) totalItineraries = itinerariesMap && itinerariesMap[vehicle?.id]?.itineraries?.length

        return toHeaderData(data, vehicle, totalTrips, totalItineraries, totalSchecules)
    }, [data, vehicle, tripsDB, appointmentsMap, itinerariesMap])

    const loadTrips = async (data, span) => {
        setIsLoading(true)
        
        const { keyMatch, collection, car_status } = data || {}

        const ruleA = keyMatch !== undefined
        const ruleB = collection !== undefined
        const ruleD = previousSpan !== undefined && previousSpan !== span

        if ((ruleA && ruleB) || ruleD) {
            
            setPreviousKeyMatch(keyMatch)            
            const tripsResponse = await getLastTrips(collection, span, keyMatch)
            const tripsSorted: any[] = sort('-end', tripsResponse.data)

            const { lat, lng } = tripsSorted.length > 0 ? tripsSorted?.[tripsSorted.length - 1] : {}

            if (car_status === 'moving') {
                // CAR IS MOVING
                const ele: any = tripsSorted[0]

                if (ele) {
                    ele.isOnGoing = true
                    tripsSorted[0] = ele
                }
            }

            setTripsDB(tripsSorted)
            setCenterMap({ lat, lng })
            setPreviousSpan(span)
        }   
        
        setIsLoading(false)
    }

    useEffect(() => {
        setTrips(tripsDB || [])
    }, [tripsDB])

    useEffect(() => {
        loadTrips(data, context?.params?.span)
    }, [data, context?.params?.span])

    return (
        <>
            {show && (
                <StyledTrips>
                    <TripsHeaderInformation
                        isLoading={isLoading}
                        name={headerData?.name}
                        plate={headerData?.plate}
                        totalTrips={headerData?.totalTrips}
                        totalItineraries={headerData?.totalItineraries}
                        totalSchedules={headerData?.totalSchedules}
                        status={headerData?.status}
                        time={headerData?.time}
                        collection={headerData.collection}
                        keyMatch={headerData.keyMatch}
                        noData={trips.length === 0}
                    />

                    <StyledContent>
                        {isLoading && <div className="loading" />}

                        {isLoading === false && (
                            <>
                                <StyledUl>
                                    <li className="flex-1">
                                        <span className="styled-ul-title">{i18n('Trips')}</span>
                                    </li>
                                </StyledUl>

                                <StyleContainerList noData={trips.length === 0}>
                                    {trips.length > 0 && (
                                        <>
                                            <TripProgress trips={trips} status={headerData?.status} />
                                            <TripsList trips={trips} />
                                        </>
                                    )}

                                    {trips.length === 0 && (
                                        <div className="no-content">
                                            <div>
                                                <i className="fas fa-empty-set"></i>
                                            </div>
                                            <span className="no-content-first">
                                                {i18n('There is no trip for the selected timespan')}.
                                            </span>
                                            <span className="no-content-second">
                                                {i18n('Select another timespam to view informations')}.
                                            </span>
                                        </div>
                                    )}
                                </StyleContainerList>
                            </>
                        )}
                    </StyledContent>
                </StyledTrips>
            )}
        </>
    )
}

export default Trips

const StyledTrips = styled.div`
    background: ${({ theme }) => theme['gray-100']};
    height: 100%;
    padding-bottom: 16px;
`

const StyledContent = styled.div`
    padding: 16px 0;
    height: calc(100% - 153px);
`

const StyledViewReport = styled.a`
    color: ${({ theme, noData }) => (noData ? theme['gray-500'] : 'auto')};
    pointer-events: ${({ noData }) => (noData ? 'none' : 'auto')};
    cursor: ${({ noData }) => (noData ? 'auto' : 'pointer')};
    font-size: 13px;
    font-weight: 600;

    .fa-file-alt {
        color: ${({ theme, noData }) => (noData ? theme['gray-500'] : theme['blue-500'])};
        margin-right: 4px;
    }
`

const StyledUl = styled.ul`
    display: flex;
    padding: 0 16px;
    align-items: center;

    li {
        color: noData;
    }

    .styled-ul-title {
        font-size: 16px;
        font-weight: 600;
        color: ${({ theme }) => theme['gray-800']};
    }
`

const StyleContainerList = styled.div`
    display: flex;
    overflow-y: scroll;
    padding-left: 21px;
    padding-right: 6px;
    height: 100%;
    align-items: ${({ noData }) => (noData ? `center` : 'initial')};
    justify-content: ${({ noData }) => (noData ? `center` : 'initial')};
    margin: ${({ noData }) => (noData ? `0 16px 16px 16px` : '0')};
    border: ${({ theme, noData }) => (noData ? `1px solid ${theme['gray-500']}` : 'none')};

    .no-content {
        text-align: center;
        padding: 0 15%;
        font-size: 13px;
        font-weight: 400;

        .no-content-first {
            font-weight: 600;
            color: ${({ theme }) => theme['gray-700']};
        }
    }

    .fa-empty-set {
        font-weight: 400;
        font-size: 40px;
        color: ${({ theme }) => theme['gray-600']};
    }

    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }

    &::-webkit-scrollbar-track {
        border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme['gray-500']};
        border-radius: 10px;
    }
`
